import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, ShieldCheck, Clock, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/api-error";
import { getAppSettings, updateAppSettings } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TIMEOUT_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const PERIOD_OPTIONS = [
  { value: "month", label: "Mese" },
  { value: "quarter", label: "Trimestre" },
  { value: "semester", label: "Semestre" },
  { value: "year", label: "Anno" },
  { value: "custom", label: "Personalizzato" },
];

const BASIS_OPTIONS = [
  { value: "cash", label: "Cassa (Pagato = incasso reale)" },
  { value: "accrual", label: "Competenza (Pagato = maturazione)" },
];

/**
 * PreferencesTab — Impostazioni → Preferenze.
 * Contiene: timeout sessione + default Cruscotto (periodo + base).
 * Le preferenze sono globali (single-user); ogni cambio scrive su
 * `app_settings` via /api/customizations/app-settings.
 */
export function PreferencesTab() {
  const { setIdleMinutes } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await getAppSettings();
        setSettings(s);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, []);

  if (!settings) {
    return (
      <div className="mt-4 text-sm text-stone-500">Caricamento…</div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateAppSettings(settings);
      setSettings(updated);
      // Aggiorna il timer locale immediatamente
      setIdleMinutes(updated.session_timeout_minutes);
      toast.success("Preferenze salvate");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="preferences-tab" className="mt-4 space-y-6">
      {/* Timeout sessione */}
      <section className="rounded-xl border border-stone-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-800">
          <Clock size={14} strokeWidth={2} className="text-primary" />
          Timeout sessione
        </div>
        <p className="mb-3 text-[12px] leading-snug text-stone-500">
          Dopo questo periodo di inattività il gestionale ti chiederà di
          reinserire la password. Movimenti del mouse non contano; contano
          click, tastiera e tocco. Consigliato: 20 minuti per uso continuo
          in studio.
        </p>
        <div className="flex items-center gap-3">
          <Label className="w-32 text-xs uppercase tracking-wider text-stone-500">
            Inattività
          </Label>
          <Select
            value={String(settings.session_timeout_minutes)}
            onValueChange={(v) =>
              setSettings({ ...settings, session_timeout_minutes: parseInt(v, 10) })
            }
          >
            <SelectTrigger
              className="w-40"
              data-testid="pref-timeout-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEOUT_OPTIONS.map((m) => (
                <SelectItem key={m} value={String(m)} data-testid={`pref-timeout-${m}`}>
                  {m} minuti
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ShieldCheck size={12} className="text-stone-400" />
          <span className="text-[11px] text-stone-500">
            Attualmente: {settings.session_timeout_minutes} min
          </span>
        </div>
      </section>

      {/* Default Cruscotto */}
      <section className="rounded-xl border border-stone-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-800">
          <LayoutDashboard size={14} strokeWidth={2} className="text-primary" />
          Default Cruscotto
        </div>
        <p className="mb-3 text-[12px] leading-snug text-stone-500">
          Preferenze applicate all&apos;apertura del Cruscotto. Puoi comunque
          cambiarle temporaneamente da lì; queste sono i valori &quot;di base&quot;
          ripristinati alla prossima apertura.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">
              Periodo predefinito
            </Label>
            <Select
              value={settings.default_period_preset}
              onValueChange={(v) =>
                setSettings({ ...settings, default_period_preset: v })
              }
            >
              <SelectTrigger
                data-testid="pref-period-trigger"
                className="mt-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    data-testid={`pref-period-${o.value}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">
              Base predefinita
            </Label>
            <Select
              value={settings.default_basis}
              onValueChange={(v) =>
                setSettings({ ...settings, default_basis: v })
              }
            >
              <SelectTrigger data-testid="pref-basis-trigger" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BASIS_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value}
                    data-testid={`pref-basis-${o.value}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="pref-save"
          className="gap-1.5"
        >
          <Save size={14} strokeWidth={2} />
          {saving ? "Salvo…" : "Salva preferenze"}
        </Button>
      </div>
    </div>
  );
}
